/**
 * Quote groups: a named, countable bundle of rooms (a 3-bedroom apartment
 * times forty, the common areas times one).
 *
 * A quote that has never been grouped still has exactly one default group
 * holding every room. Hiding that group's chrome is what keeps a residential
 * quote looking the way it always has.
 */

import { MAX_GROUP_QUANTITY } from '../constants/index.ts';

export interface QuoteGroupLike {
  readonly id: string;
  readonly name: string;
  readonly quantity: number;
  readonly notes?: string | null;
  readonly isDefault: boolean;
}

export function clampGroupQuantity(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) return 1;
  return Math.min(MAX_GROUP_QUANTITY, Math.max(1, Math.trunc(value)));
}

/**
 * The group UI stays out of the way while the quote is one untouched default
 * group. Naming it, counting it, or adding another is what brings the chrome
 * out — and what a customer then sees as sections.
 */
export function groupsChromeVisible(groups: readonly QuoteGroupLike[] | null | undefined): boolean {
  if (!groups || groups.length === 0) return false;
  if (groups.length > 1) return true;
  const only = groups[0];
  if (!only) return false;
  if (!only.isDefault) return true;
  if (clampGroupQuantity(only.quantity) !== 1) return true;
  if (only.name.trim().length > 0) return true;
  if ((only.notes ?? '').trim().length > 0) return true;
  return false;
}

export function defaultGroupOf<T extends QuoteGroupLike>(groups: readonly T[]): T | undefined {
  return groups.find((group) => group.isDefault) ?? groups[0];
}
